import { type SubmitEvent, useState } from "react";
import type { SxProps, Theme } from "@mui/material";
import { CardContent } from "@mui/material";
import ModalHeader from "src/components/general/Modal/components/ModalHeader/ModalHeader";
import Modal from "src/components/general/Modal/Modal";
import NavContent from "src/components/general/NavContent/NavContent";
import WrapIf from "src/components/utils/WrapIf/WrapIf";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import type { ModalWithNavigationProps } from "./ModalWithNavigation.types";

export default function ModalWithNavigation({
  title,
  sections,
  children,
  isForm,
  noValidate = true,
  onSubmit,
  loading,
  testId,
  slots,
  slotProps,
  isOpen,
  onClose,
  sx,
  initialSection,
  onSectionChange,
}: ModalWithNavigationProps) {
  const Header = slots?.Header ?? ModalHeader;
  const Footer = slots?.Footer;

  const [activeSection, setActiveSection] = useState("");

  const resolvedSx = {
    ...(typeof sx === "function"
      ? (sx as (s: string) => SxProps<Theme>)(activeSection)
      : sx),
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    onSectionChange?.(section);
  };

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit?.(e);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} sx={resolvedSx} testId={testId}>
      <Header {...slotProps?.header} title={title} onClose={onClose} />

      <CardContent className="flex-1 overflow-hidden p-0! bg-background-default">
        {loading ? (
          <LoadingIndicator loading />
        ) : (
          <WrapIf
            condition={!!isForm}
            With="form"
            className="flex h-full flex-col"
            onSubmit={handleSubmit}
            noValidate={noValidate}
          >
            <NavContent
              sections={sections}
              initialSection={initialSection}
              onSectionChange={handleSectionChange}
            >
              {(selectedSection) => (
                <>
                  <div
                    className="flex-1 overflow-auto p-6 scrollShadow *:mix-blend-color"
                    style={
                      {
                        "--shadow-color": "var(--color-background-default)",
                      } as React.CSSProperties
                    }
                  >
                    {typeof children === "function"
                      ? children(selectedSection)
                      : children}
                  </div>
                  {(() => {
                    const footerContent =
                      typeof Footer === "function"
                        ? Footer(selectedSection)
                        : Footer;
                    return (
                      footerContent && (
                        <div className="shrink-0">{footerContent}</div>
                      )
                    );
                  })()}
                </>
              )}
            </NavContent>
          </WrapIf>
        )}
      </CardContent>
    </Modal>
  );
}
